import { diffText } from '@lyrmo/shared';
import { useMemo, type CSSProperties } from 'react';
import { useI18n } from '../../i18n/i18n-context';
import { parseMarkdown, readableSlice } from './markdown';
import { MarkdownText, type TextExtra, type TextStretch } from './MarkdownText';
import { segmentPlaces } from './version-changes';

interface DiffViewProps {
  readonly original: string;
  readonly rewritten: string;
  /** Numbers to show after diff segments, keyed by segment index: the explained changes. */
  readonly marks?: ReadonlyMap<number, number>;
}

/**
 * The rewrite, formatted as its text is, its additions marked where they are and the words it
 * took out struck through where they were.
 */
export function DiffView({ original, rewritten, marks }: DiffViewProps) {
  const { t } = useI18n();
  const segments = useMemo(() => diffText(original, rewritten), [original, rewritten]);
  const [stretches, extras] = useMemo(() => {
    const originalLines = parseMarkdown(original);
    const rewrittenLines = parseMarkdown(rewritten);
    const places = segmentPlaces(segments);
    const stretches: TextStretch[] = [];
    const extras: TextExtra[] = [];
    for (const [index, { type, text }] of segments.entries()) {
      const { before, after } = places[index] ?? { before: 0, after: 0 };
      // Its place in the text, so the changes mark themselves in reading order.
      const style = { '--part': index } as CSSProperties;
      if (type === 'insert') {
        if (readableSlice(rewritten, after, after + text.length, rewrittenLines))
          extras.push({ at: after, node: <span className="sr-only">{t.diff.added}</span> });
        stretches.push({
          start: after,
          end: after + text.length,
          draw: (children) => <ins style={style}>{children}</ins>,
        });
      } else if (type === 'delete') {
        const words = readableSlice(original, before, before + text.length, originalLines);
        if (words)
          extras.push({
            at: after,
            node: (
              <>
                <span className="sr-only">{t.diff.deleted}</span>
                <del style={style}>{words}</del>
              </>
            ),
          });
      }
      const mark = marks?.get(index);
      if (mark !== undefined)
        extras.push({
          at: type === 'insert' ? after + text.length : after,
          node: (
            <sup className="change-mark" aria-hidden="true">
              {mark}
            </sup>
          ),
        });
    }
    return [stretches, extras];
  }, [marks, original, rewritten, segments, t]);

  return <MarkdownText text={rewritten} stretches={stretches} extras={extras} />;
}
